import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useBlurValidation } from '@/ui/validation'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { STEP_META, pathFor } from './steps'

/**
 * Mirrors `SiteRequest.domain` in `openapi.json` (`minLength: 3, maxLength: 300`).
 * Bounded here as well as on the server because a 422 arrives as "Request
 * validation failed" with no field named, and this step has three controls
 * (WEB-14).
 */
const DOMAIN_MIN = 3
const DOMAIN_MAX = 300

/**
 * `data-validate="required|url"` on the Atlas control (`onboarding.html:85`), as
 * the two sentences the Atlas validator writes (`design/atlas/components.js:212`,
 * `:247`) — ONB-1.
 *
 * **Deliberately permissive**, for the reason `looksLikeEmail` gives: the server
 * strips the scheme, the path and the `www.` (`app/onboarding/domains.py`) and is
 * the authority on what a domain is. This exists to catch an empty box and
 * "kavya interiors" before a round trip, not to adjudicate. A label, a dot and
 * something either side is the whole test — anything stricter would start
 * rejecting the internationalised and long-suffix domains the server accepts, and
 * a client refusing what the server would have stored is the worse failure.
 */
function domainProblem(value: string): string | undefined {
  if (value === '') return 'This field is required.'
  const bare = value
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/?#]/)[0]
  if (bare === undefined || bare.length < DOMAIN_MIN || !/^[^\s.]+(\.[^\s.]+)+$/.test(bare)) {
    return 'Enter a domain, e.g. kavyainteriors.in'
  }
  return undefined
}

/**
 * Step 1 — S-D2's `s1`. The only step that cannot be skipped.
 *
 * The domain is sent **as typed**: normalising it here would be a second
 * implementation of `app/onboarding/domains.py` that would eventually disagree
 * with it, and the disagreement would show up as a site the tenant cannot find.
 * The server strips the scheme, the path and the `www.` and answers with what it
 * stored, which is what the field is refilled from.
 *
 * The approval checkbox defaults to ticked and the copy says why. Approval-gated
 * publishing is a CLAUDE.md invariant, not a preference with a neutral default.
 */
export function SiteStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const [domain, setDomain] = useState(state.site?.domain ?? '')
  const [description, setDescription] = useState(state.site?.business_description ?? '')
  const [requireApproval, setRequireApproval] = useState(state.site?.require_approval ?? true)

  // Measured on the trimmed value, because that is what `onSubmit` sends.
  //
  // ONB-1. Shown **on blur, then live once wrong** — `useBlurValidation`, the
  // same rule S-D2's signup and reset screens follow, and the timing the Atlas
  // control's `data-validate` implements. It used to be silent until three
  // characters had been typed and then said "At least 3 characters.", which
  // meant an empty required field said nothing at all: the only refusal a
  // tenant who tabbed past the box ever saw was the server's, rendered as a
  // form summary above the card rather than under the control it is about.
  // `Field` has rendered a per-field message and `data-state="invalid"` all
  // along (`Field.tsx:60-65`); nothing was passing it one.
  const trimmedDomain = domain.trim()
  const website = useBlurValidation(domainProblem(trimmedDomain))

  const save = useMutation(
    (body: { domain: string; business_description: string | null; require_approval: boolean }) =>
      call(() => api.PUT('/api/v1/onboarding/site', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    // `error`, not `shown`: pressing Enter from the description field is a
    // departure from this one too, and posting a value already judged invalid
    // would spend a round trip being told what the client knew.
    if (website.error !== undefined) {
      website.onBlur()
      return
    }
    const result = await save.run({
      domain: domain.trim(),
      business_description: description.trim() || null,
      require_approval: requireApproval,
    })
    if (result.ok) {
      adopt(result.data)
      // A site added after setup finished gets its own three per-site steps
      // (settings consolidation, 2026-09-15) rather than a wizard with nothing
      // outstanding to show. By the typed domain: the response's `site` is the
      // wizard's first site, not this one.
      void navigate(
        result.data.completed_at !== null
          ? `/seo/sites/setup/${encodeURIComponent(domain.trim())}`
          : pathFor(result.data.current_step),
      )
    }
  }

  return (
    <form
      className="app-wiz"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <WizardChrome current="site" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">{STEP_META.site.title}</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              We&rsquo;ll run a technical audit straight away — it takes a few minutes and
              doesn&rsquo;t need anything from you.
            </p>
          </div>

          <InlineError failure={save.state.failure} />

          <TextField
            label="Website"
            required
            // The one wizard label the mockup marks (`onboarding.html:83`).
            mark
            autoFocus
            inputMode="url"
            maxLength={DOMAIN_MAX}
            error={website.shown}
            onBlur={website.onBlur}
            placeholder="kavyainteriors.in"
            help="Domain only — no https:// needed."
            value={domain}
            onChange={(event) => {
              setDomain(event.target.value)
              save.reset()
            }}
          />

          <TextField
            label="What does the business do?"
            help="One line. This shapes which keywords we suggest."
            placeholder="Modular kitchens and full-home interiors, Hyderabad"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
            }}
          />

          <div className="ar-panel">
            <label className="ar-choice ar-choice--check" style={{ padding: 0 }}>
              <input
                type="checkbox"
                checked={requireApproval}
                onChange={(event) => {
                  setRequireApproval(event.target.checked)
                }}
              />
              <span className="ar-choice__box" aria-hidden="true" />
              <span className="ar-choice__text">
                Require my approval before anything is published
                {/* Parity P-ONB1-05: the mockup's two sentences. What the
                    approval covers, and how to set it per kind, is said on
                    SEO → Approvals itself. */}
                <span className="ar-choice__desc">
                  Strongly recommended. You can change this later in Settings, but we default to
                  asking.
                </span>
              </span>
            </label>
          </div>
        </div>

        <WizardFooter current="site">
          <Button tone="primary" type="submit" busy={save.pending} busyLabel="Saving your site">
            Continue
          </Button>
        </WizardFooter>
      </section>
    </form>
  )
}
